/** * チェックインカードコンポーネント */
<template>
  <div class="card-checkin">
    <!-- ユーザー -->
    <card-user
      can-user-name-click
      v-bind:user-id="userId"
      v-bind:user-name="userName"
      v-bind:profile-image-attach-file-type="profileImageAttachFileType"
      v-bind:update-date="updateDate"
      v-bind:groups="groups"
      v-on:usercard-username-click="
        $store.dispatch('updateProfileUserId', $event)
      "
    />
    <b-card-text
      align="left"
      v-on:click="$_onClickCard"
      class="event-card-body"
    >
      <div class="event-card-body-left">
        <!-- チェックインタイトル -->
        <div class="event-card-title">
          <icon-checkin class="icon-checkin" />
          <p>
            {{ prefixText }}
            <span v-on:click="$_onClickTitle">
              {{ placeName }}
            </span>
            {{ postfixText }}
          </p>
        </div>
        <div class="update-at-text">{{ checkinUpdateAt }}</div>
        <!-- チェックインコメント -->
        <p class="event-card-description">
          {{ comment }}
        </p>
      </div>
    </b-card-text>
  </div>
</template>

<script>
import CardUser from '@/components/AppCardUser.vue'
import IconCheckin from '@/assets/ifield/ic_checkin.svg?component'
import { useDateDisplayText } from '@/composables/UseDateDisplayText'
import { toRefs } from 'vue'

// タイムラインタイプ
const TIMELINE_TYPE = 4

export default {
  name: 'AppCardCheckin',
  props: {
    // タイムラインID、チェックインID(一意となるIDを設定する)
    id: {
      type: Number,
      required: true,
    },
    // ユーザID
    userId: {
      type: Number,
      required: true,
    },
    // チェックイン更新日時
    updateAt: {
      type: String,
      required: true,
    },
    // プレイス名
    placeName: {
      type: String,
      required: true,
    },
    // コメント
    comment: {
      type: String,
      default: null,
    },
    // 送信者プロフィール画像の添付ファイルタイプ
    profileImageAttachFileType: {
      type: String,
      default: '9',
    },
    // 送信ユーザ名
    userName: {
      type: String,
      required: true,
    },
    // サブタイプ（チェックイン：１、チェックアウト：２）
    timelineSubtype: {
      type: String,
      default: '',
    },
    /**
     * 基準日時
     */
    baseDate: {
      type: Date,
      default: null,
    },
    /**
     * 所属グループ
     */
    groups: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    'card-user': CardUser,
    'icon-checkin': IconCheckin,
  },
  data: function () {
    return {}
  },
  setup(props) {
    // チェックイン日時の表示テキスト
    const { updateAt, baseDate } = toRefs(props)
    const { text } = useDateDisplayText({ date: updateAt, baseDate })
    return { checkinUpdateAt: text }
  },
  computed: {
    /**
     * チェックイン更新日時
     * @type {Date}
     */
    updateDate: function () {
      return this.updateAt ? new Date(this.updateAt) : null
    },
    /**
     * チェックインタイトルの前に追加する文字列
     * @type {String}
     */
    prefixText: function () {
      if (!this.timelineSubtype) {
        return ''
      } else if (this.timelineSubtype === '1') {
        return this.$t('appCardCheckin.checkinTitlePrefix')
      } else {
        return this.$t('appCardCheckin.checkoutTitlePrefix')
      }
    },
    /**
     * チェックインタイトルの後ろに追加する文字列
     * @type {String}
     */
    postfixText: function () {
      if (!this.timelineSubtype) {
        return ''
      } else if (this.timelineSubtype === '1') {
        return this.$t('appCardCheckin.checkinTitlePostfix')
      } else {
        return this.$t('appCardCheckin.checkoutTitlePostfix')
      }
    },
  },
  methods: {
    /**
     * クリック時のイベント
     */
    $_onClickCard: function () {
      this.$emit('checkincard-click', TIMELINE_TYPE, this.id)
    },
    /**
     * タイトルクリック時のイベント
     */
    $_onClickTitle: function (event) {
      event.stopPropagation()
      this.$emit('checkincard-title-click', TIMELINE_TYPE, this.id)
    },
  },
}
</script>

<style lang="scss" scoped>
.card-checkin {
  height: 210px;
}

::v-deep .card-user {
  padding-right: 8px;
}

.event-card-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: auto;
  padding: 0;
  margin: 10px 0 0 0;
  background-color: #f6f6f6;
  border-radius: 2px;
  overflow: hidden;
  transition: 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
  }
}

.event-card-body-left {
  width: 100%;
  height: 160px;
  padding: 24px 20px 20px 20px;
}

.event-card-title {
  display: flex;
  justify-content: flex-start;
  align-items: center;

  p {
    margin-bottom: 0;
    line-height: 20px;
    color: #4d4d4d;
    font-size: 0.9em !important;
  }

  span {
    color: $text-color-link;
    font-weight: bold;
    margin-right: 4px;

    &:hover {
      text-decoration: underline;
    }
  }
}

.update-at-text {
  font-size: $font-size-small;
  color: #959595;
  margin: 8px 0 16px 0;
}

.event-card-description {
  margin-bottom: 15px;
  color: #4d4d4d;
  font-size: 0.8em;
  line-height: 1.4;
  text-align: left;

  @supports (-webkit-line-clamp: 3) {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
}

.icon-checkin {
  height: 20px;
  width: 20px;
  padding: 3px;
  margin-right: 12px;
  border-radius: 50%;
  position: relative;
  fill: #654928;
  background-color: $entity-color-yellow;
  flex-shrink: 0;
}
</style>
